/**
 * BondLayer - Renders bonds from SVG generation data
 *
 * Renders hitboxes (if provided) and visuals in the same group.
 * Hitboxes are transparent with pointer-events; visuals have pointer-events: none.
 *
 * Supports opt-in viewport culling (PIPH-72) via `ViewportCullingContext`.
 * When a culling provider is present, bonds whose endpoints both lie
 * outside the viewport (plus margin equal to one bond length) are skipped
 * entirely. A bond with at least one endpoint inside the viewport always
 * renders — motion values for both endpoints remain available in
 * `MotionContext` regardless of whether the endpoint's visual is culled
 * by `AtomLayer`, so the bond can still position itself correctly.
 */

import React, { memo, useMemo } from 'react';
import { useSVGGenerationData } from '../../contexts/SVGGenerationContext';
import { useViewportCulling } from '../../contexts/ViewportCullingContext';
import { DynamicBond } from '../../motion/components/DynamicBond';
import { isBondInViewport } from '../../rendering/viewportCulling';
import type { BondRecipe } from '../../rendering/types/bondRecipe.types';
import type { AtomRenderData } from '../../rendering/useSVGGeneration';

export interface BondLayerProps {
  render?: (recipe: BondRecipe, atoms: AtomRenderData[]) => React.ReactNode;
  renderHitbox?: (recipe: BondRecipe, atoms: AtomRenderData[]) => React.ReactNode;
}

// React.memo (PIPH-69): see AtomLayer for rationale.
export const BondLayer = memo(function BondLayer({ render, renderHitbox }: BondLayerProps) {
  const { bondRecipes, atoms } = useSVGGenerationData();
  const culling = useViewportCulling();

  // Build atomId → atom lookup once per render. Used for:
  //   (a) viewport culling (needs endpoint positions)
  //   (b) the default visibility check below (which previously did
  //       atoms.find(...) per bond — O(n·m) → O(n + m))
  const atomsById = useMemo(() => {
    const map = new Map<string, AtomRenderData>();
    for (const a of atoms) map.set(a.id, a);
    return map;
  }, [atoms]);

  // Apply viewport culling when a ViewportCullingProvider is present.
  // Without a provider, `culling` is null and we render every bond.
  const visibleBondRecipes = useMemo(() => {
    if (!culling) return bondRecipes;
    return bondRecipes.filter((recipe) =>
      isBondInViewport(recipe, atomsById, culling.bounds, culling.margin),
    );
  }, [bondRecipes, atomsById, culling]);

  return (
    <g data-layer="bonds">
      {visibleBondRecipes.map((recipe) => {
        // Default visibility check — skip bonds to hidden atoms.
        if (!render) {
          const atom1 = atomsById.get(recipe.atom1Id);
          const atom2 = atomsById.get(recipe.atom2Id);
          if (atom1?.visibility === 'hidden' || atom2?.visibility === 'hidden') {
            return null;
          }
        }

        return (
          <React.Fragment key={recipe.bondId}>
            {renderHitbox && renderHitbox(recipe, atoms)}
            {render ? render(recipe, atoms) : <DynamicBond recipe={recipe} />}
          </React.Fragment>
        );
      })}
    </g>
  );
});
